import React from 'react';
import { Modal, Typography, Space, Divider } from 'antd';
import { WarningOutlined, FullscreenOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface FullscreenWarningModalProps {
  visible: boolean;
  onAcknowledge: () => void;
  onCancel: () => void;
}

/**
 * Modal component that warns participants about fullscreen requirements
 * Must be acknowledged before assessment can proceed
 */
export const FullscreenWarningModal: React.FC<FullscreenWarningModalProps> = ({
  visible,
  onAcknowledge,
  onCancel,
}) => {
  return (
    <Modal
      open={visible}
      title={null}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      centered
      width={600}
    >
      <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }}>
        <div
          style={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            backgroundColor: '#fff7e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
          }}
        >
          <FullscreenOutlined style={{ fontSize: 40, color: '#fa8c16' }} />
        </div>

        <Title level={3} style={{ margin: 0 }}>
          Fullscreen Mode Required
        </Title>

        <Paragraph style={{ fontSize: 16, marginBottom: 0 }}>
          This assessment must be completed in fullscreen mode to ensure a distraction-free
          environment and maintain assessment integrity.
        </Paragraph>

        <div
          style={{
            backgroundColor: '#fff1f0',
            border: '2px solid #ff4d4f',
            borderRadius: 8,
            padding: 20,
            boxShadow: '0 4px 12px rgba(255, 77, 79, 0.1)'
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}>
              <WarningOutlined style={{ color: '#ff4d4f', fontSize: 24 }} />
              <Text strong style={{ color: '#ff4d4f', fontSize: 18, textTransform: 'uppercase' }}>
                Strict Termination Policy
              </Text>
            </div>
            <Divider style={{ margin: '12px 0', borderColor: '#ffa39e' }} />
            <Text style={{ fontSize: 15, textAlign: 'center', display: 'block' }}>
              Any attempt to <Text strong>exit fullscreen</Text>, <Text strong>switch tabs</Text>, <Text strong>switch desktops</Text>, or <Text strong>lose window focus</Text> will result in
              <Text strong style={{ color: '#cf1322' }}> IMMEDIATE TERMINATION </Text>
              of your assessment.
            </Text>
          </Space>
        </div>

        <Paragraph style={{ fontSize: 14, color: '#8c8c8c', marginBottom: 0 }}>
          Please ensure you are ready to complete the assessment without interruption before
          proceeding.
        </Paragraph>

        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <button
            onClick={onAcknowledge}
            style={{
              width: '100%',
              padding: '12px 24px',
              fontSize: 16,
              fontWeight: 600,
              color: '#fff',
              backgroundColor: '#fa541c',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#d4380d';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#fa541c';
            }}
          >
            I Understand - Begin Assessment
          </button>
          <button
            onClick={onCancel}
            style={{
              width: '100%',
              padding: '10px 24px',
              fontSize: 14,
              fontWeight: 500,
              color: '#595959',
              backgroundColor: '#f5f5f5',
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              cursor: 'pointer',
              transition: 'all 0.3s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#e8e8e8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#f5f5f5';
            }}
          >
            Cancel
          </button>
        </Space>
      </Space>
    </Modal>
  );
};
